import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useBrand } from '../contexts/BrandContext';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { BookOpen, Clock, Eye, ArrowRight, Tag, Headphones, Search } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BlogPage() {
    const brand = useBrand();
    const [posts, setPosts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [selectedCat, setSelectedCat] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchPosts();
        fetchCategories();
    }, [selectedCat]);

    const fetchPosts = async () => {
        try {
            const params = selectedCat ? `?category=${encodeURIComponent(selectedCat)}` : '';
            const res = await fetch(`${API}/api/blog/posts${params}`);
            const data = await res.json();
            setPosts(data.posts || []);
        } catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const fetchCategories = async () => {
        try {
            const res = await fetch(`${API}/api/blog/categories`);
            setCategories(await res.json());
        } catch (e) { console.error(e); }
    };

    return (
        <div className="min-h-screen bg-[#0A0D14]">
            <SEO
                title="Travel Insider — Blog & Guides"
                description="Expert travel guides, insider tips, and destination deep-dives. Plan your perfect trip with Voxoora's curated travel content."
            />
            <header className="fixed top-0 left-0 right-0 z-50 glass">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
                    <Link to="/" className="flex items-center gap-2">
                        {brand.logo_url ? (
                            <img src={brand.logo_url} alt={brand.platform_name} className="h-7 sm:h-9 max-w-[120px] sm:max-w-[160px] object-contain" />
                        ) : (
                            <span className="font-outfit font-semibold text-white text-lg">{brand.platform_name}</span>
                        )}
                    </Link>
                    <Link to="/"><Button variant="ghost" size="sm" className="text-white/50 hover:text-white gap-1 text-xs"><Headphones className="w-3.5 h-3.5" /> Explore Experiences</Button></Link>
                </div>
            </header>

            <div className="pt-20 pb-16 px-4 sm:px-6">
                <div className="max-w-6xl mx-auto">
                    {/* Hero */}
                    <div className="text-center mb-10 sm:mb-14 pt-6">
                        <p className="text-gold uppercase tracking-widest text-xs mb-3">Travel Insider</p>
                        <h1 className="font-outfit text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-3">Guides, Tips & Stories</h1>
                        <p className="text-muted-foreground max-w-lg mx-auto">Expert travel guides, destination deep-dives, and insider secrets to help you explore the world's greatest cultural treasures.</p>
                    </div>

                    {/* Category Filter */}
                    <div className="flex gap-2 flex-wrap justify-center mb-10">
                        <button onClick={() => setSelectedCat('')}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${!selectedCat ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/5 text-white/50 border-white/10 hover:border-white/20'}`}>
                            All Posts
                        </button>
                        {categories.map(cat => (
                            <button key={cat.name} onClick={() => setSelectedCat(cat.name)}
                                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${selectedCat === cat.name ? 'bg-gold/15 text-gold border-gold/30' : 'bg-white/5 text-white/50 border-white/10 hover:border-white/20'}`}>
                                {cat.name} ({cat.count})
                            </button>
                        ))}
                    </div>

                    {/* Posts Grid */}
                    {loading ? (
                        <div className="text-center py-16 text-white/20">Loading...</div>
                    ) : posts.length === 0 ? (
                        <div className="text-center py-16 text-white/20">No posts found</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                            {posts.map((post, i) => (
                                <Link key={post.id || post.slug} to={`/blog/${post.slug}`}
                                    className="group bg-[#121620] border border-white/10 rounded-2xl overflow-hidden hover:border-gold/20 transition-all duration-300 hover:-translate-y-1"
                                    data-testid={`blog-card-${post.slug}`}>
                                    <div className="aspect-[16/9] overflow-hidden bg-white/5">
                                        {post.cover_image ? (
                                            <img src={post.cover_image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center"><BookOpen className="w-8 h-8 text-white/10" /></div>
                                        )}
                                    </div>
                                    <div className="p-5">
                                        <div className="flex items-center gap-2 mb-3">
                                            {post.category && <span className="text-[10px] text-gold bg-gold/10 px-2 py-0.5 rounded-full">{post.category}</span>}
                                            <span className="text-[10px] text-white/30 flex items-center gap-1"><Clock className="w-2.5 h-2.5" />{post.reading_time || 1} min read</span>
                                        </div>
                                        <h2 className="font-outfit font-semibold text-white text-base mb-2 group-hover:text-gold transition-colors line-clamp-2">{post.title}</h2>
                                        <p className="text-xs text-white/40 line-clamp-2 mb-3">{post.excerpt}</p>
                                        <div className="flex items-center justify-between">
                                            <span className="text-[10px] text-white/20">{post.author_name}</span>
                                            <span className="text-gold text-xs flex items-center gap-1 group-hover:gap-2 transition-all">Read <ArrowRight className="w-3 h-3" /></span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
